/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectIsPrimary,
  selectIsTrendDeAggregate,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectSegmentSetAName,
  selectSegmentSetBName,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectTrendHotelFocusOn,
  selectTrendHotelNormalizedView,
  selectTrendGranularity,
  selectEnableSecondaryHotelGroups,
  selectTrendData,
  selectTrendDataResponseId,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import MuiTabular from 'modules/common/components/mui-tabular';
import { Card, Grid, IconButton, Typography } from '@mui/material';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import { API, EXPORT_OPTIONS } from 'modules/dashboard/constants';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import DownloadIcon from '@mui/icons-material/Download';
import { useDebouncedCallback } from 'use-debounce';
import { AssistantIcon } from 'modules/common/components';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { ENVIRONMENT } from 'config';
import CollapseIcon from 'modules/common/components/collapse-icon';
import {
  BREAKDOWN_LIST_GETTER_COLUMNS,
  TREND_HOTEL_CUMULATIVE_FORECAST_TABLE_COLUMNS,
  TREND_HOTEL_CUMULATIVE_TABLE_COLUMNS,
  TREND_HOTEL_MOVING_AVERAGE_TABLE_COLUMNS,
  TREND_HOTEL_PERCENTAGE_TABLE_COLUMNS,
  TREND_HOTEL_WEEKLY_FORECAST_TABLE_COLUMNS,
  TREND_HOTEL_TABLE_COLUMNS,
  TREND_HOTEL_WEEKLY_TABLE_COLUMNS,
} from '../../../../constants';
import TabularFilterHeader from '../../filter-header';
import { trendActions } from '../../../../slice';
import {
  buildSegmentSchema,
  buildTrendFilters,
  buildTrendHeaderData,
  getBreakdownName,
  getColumns,
  handleCeruleanLogging,
} from '../../../../functions';
/**
 * Trend Hotel tabular visual report with filters
 */

const MuiTrendHotelTabularWidget = ({ setIsHotelLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const normalization = useSelector(selectTrendHotelNormalizedView);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isBreakdown = useSelector(selectIsPrimary);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const segmentGroupA = useSelector(selectSegmentGroup1);
  const segmentGroupB = useSelector(selectSegmentGroup2);
  const segmentGroupAName = useSelector(selectSegmentSetAName);
  const segmentGroupBName = useSelector(selectSegmentSetBName);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const trendGranularity = useSelector(selectTrendGranularity);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  //
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isDisplay, setIsDisplay] = useState(true);
  //
  const [isHotelComparison] = useState(true);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  //
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      hotelFocusOn,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      segmentGroupA,
      segmentGroupB,
      segmentGroupAName,
      segmentGroupBName,
      resetBookingDate,
      normalization,
      trendGranularity,
      partialHours,
      lastBookingDate,
    });
    //
    const columns = getColumns({
      isPercentageChange,
      isMovingAverage,
      isTrendDeAggregate,
      isCumulative,
      isWeeklyView,
      isForecast,
      isHotelComparison,
    });
    //
    let start;
    try {
      setIsHotelLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Hotel Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      //
      const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
        organizationId,
        columns: BREAKDOWN_LIST_GETTER_COLUMNS,
        filters,
      });
      const breakdown = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
      const breakdownList = breakdownListData?.map((item) => item?.[breakdown]);
      if (filters?.breakDownList?.conditions) {
        const isTotalInclude = filters.breakDownList.conditions.values
          .map((v) => v.replace(/\u200B/g, '').trim())
          .includes('Total');
        filters.breakDownList.conditions.values = [
          ...breakdownList,
          ...(isTotalInclude ? ['Total'] : []),
        ];
      }
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-hotel',
      });
      dispatch(
        trendActions.getTrendData({
          organizationId,
          columns,
          // columns: [{ table: 'measures', columns: 'trend_tabular_hotel_daily' }],
          filters,
        })
      );
    } catch (error) {
      if (isRequestCanceled(error)) return;
      handleCeruleanLogging({
        message: `Error fetching trend hotel tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsHotelLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    if (isDisplay) {
      fetchData();
    } else {
      // a visual that is not displayed has nothing to load
      setIsHotelLoaded(true);
    }
  }, [
    bookingDate,
    currencyType,
    hotels,
    hotelFocusOn,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsHotelLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    normalization,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    isHotelComparison,
    startDate,
    endDate,
    isBreakdown,
    isTrendDeAggregate,
    segmentGroupA,
    segmentGroupB,
    segmentGroupAName,
    segmentGroupBName,
    trendGranularity,
    partialHours,
    lastBookingDate,
    isDisplay,
  ]);
  //
  const headerData = useMemo(
    () =>
      buildTrendHeaderData({
        hotelName: hotels?.label,
        hotelFocusOn: hotelFocusOn?.label,
        isComparisonHotelSet: Boolean(hotelFocusOn?.hotels),
        bookingDate,
        targetStartDate,
        targetEndDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        pmsSync: pms,
        normalization,
        dateComparison,
        startDate,
        endDate,
        isBreakdown,
        isCumulative,
        isPercentageChange,
        isWeeklyView,
        isMovingAverage,
        isHotelComparison,
        resetBookingDate,
        partialHours,
        lastBookingDate,
      }),
    [
      hotels,
      hotelFocusOn,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      normalization,
      dateComparison,
      isBreakdown,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      trendGranularity,
      partialHours,
      lastBookingDate,
    ]
  );
  const tableColumns = () => {
    const rowKeys = Object.keys(trendData?.[0]);
    const granularity = rowKeys?.find((val) => ['day_name', 'date_name', 'StayKey']?.includes(val));

    if (isBreakdown && !isTrendDeAggregate) {
      return buildSegmentSchema(trendGranularity, trendData?.[0], isWeeklyView);
    }
    if (isCumulative && isForecast) {
      return TREND_HOTEL_CUMULATIVE_FORECAST_TABLE_COLUMNS(granularity);
    }
    if (isWeeklyView && isForecast) {
      return TREND_HOTEL_WEEKLY_FORECAST_TABLE_COLUMNS;
    }
    if (isCumulative && !isWeeklyView) {
      return TREND_HOTEL_CUMULATIVE_TABLE_COLUMNS(granularity);
    }
    if (isMovingAverage && isPercentageChange) {
      return TREND_HOTEL_PERCENTAGE_TABLE_COLUMNS;
    }
    if (isMovingAverage) {
      return TREND_HOTEL_MOVING_AVERAGE_TABLE_COLUMNS;
    }
    if (isWeeklyView) {
      return TREND_HOTEL_WEEKLY_TABLE_COLUMNS;
    }
    return TREND_HOTEL_TABLE_COLUMNS;
  };
  //
  const onClick = () => {
    setIsDisplay(!isDisplay);
  };
  //
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      className={styles.mainGrid}
      mb={10}
    >
      <Card
        sx={{
          width: '100%',
          margin: '1rem 8rem',
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={40}
          width="100%"
        >
          <Grid item pl={2}>
            {headerData?.title}
          </Grid>
          <Grid item>
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              width="100%"
            >
              {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                <Grid item mt={1} mr={4}>
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || !isDisplay || isExporting}
                    isCerulean
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                </Grid>
              )}

              <IconButton
                size="medium"
                onClick={() => setIsProcessing(true)}
                disabled={!isDisplay || !isLoaded || isExporting}
                sx={EXPORT_BUTTON_STYLE}
                id="trend-time-js-export-button"
              >
                <Typography
                  sx={{
                    mr: 1,
                  }}
                >
                  Export Data
                </Typography>
                <DownloadIcon />
              </IconButton>

              <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
            </Grid>
          </Grid>
        </Grid>

        {isDisplay &&
          (!trendData ? (
            <Typography fontWeight={500} pb={4} textAlign="center">
              Loading Data...
            </Typography>
          ) : (
            <MuiTabular
              ref={tableRef}
              columns={tableColumns()}
              isProcessing={isProcessing}
              isLoadingData={!isLoaded}
              setIsProcessing={setIsProcessing}
              isAggregate={!(isMovingAverage || isPercentageChange)}
              headerComponent={
                <TabularFilterHeader
                  title=""
                  hotelName={headerData?.hotelName}
                  isHotelSet={Boolean(hotels?.hotels)}
                  comparisonHotelName={headerData?.comparisonHotelName}
                  isComparisonHotelSet={headerData?.isComparisonHotelSet}
                  sections={headerData?.sections}
                  chunkSize={1}
                />
              }
              data={trendData}
              headerFiltersInfo={headerData}
              customBackgroundStyle={{
                paddingLeft: 3,
                maxWidth: { md: '72vw', lg: '76vw' },
                paddingBottom: 8,
              }}
              customHeaderStyle={isBreakdown ? { lineHeight: 1.2 } : {}}
              fileName={
                isMovingAverage
                  ? isPercentageChange
                    ? 'TVH_Percentage'
                    : 'CMATH_Daily'
                  : isCumulative
                  ? 'TVH_Cumulative'
                  : 'TVH_Daily'
              }
              primaryTextColor={isPercentageChange ? '#000' : undefined}
              secondaryTextColor={isPercentageChange ? '#000' : undefined}
              removeEmptyColumns={isBreakdown}
            />
          ))}
      </Card>
    </Grid>
  );
};
//
export default MuiTrendHotelTabularWidget;
